import React from 'react';
import { Alert, Button, Space, Typography } from 'antd';

/**
 * The blast wall around this module.
 *
 * A render error inside a drawer used to take the whole admin down: opening
 * "Prompt and answer" on a model call threw "Objects are not valid as a React
 * child", React unmounted the tree it could not repair, and what was left was
 * the application's Bootstrap error page — no navigation, no tabs, no way back
 * except a browser reload. The bug that caused it is fixed; this exists so the
 * next one costs a panel instead of the session.
 *
 * Two things it deliberately does NOT do. It does not retry by itself, because
 * a component that threw once on the same props throws again and a silent loop
 * is worse than a message. And it does not swallow the error — it prints the
 * message, because the person reading it is an admin sitting next to an
 * engineer, and "something went wrong" wastes both of them.
 */

type Props = {
  /** What broke, in the admin's words: "the model call drawer". */
  what: string;
  children: React.ReactNode;
};

type State = { error: Error | null };

class ScenarioErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Kept out of the UI and in the console, where a developer looks: the
    // component stack is the only part of this that names the file.
    // eslint-disable-next-line no-console
    console.error('Scenario builder render error', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    const { what, children } = this.props;
    if (!error) return children;
    return (
      <Alert
        className="assessment-callout"
        type="error"
        showIcon
        data-testid="scenario-error-boundary"
        title={`${what} could not be displayed`}
        description={
          <Space direction="vertical" size={8}>
            <Typography.Text>
              Nothing was changed. The rest of this screen still works — close
              this panel and carry on, or reload the page to try again.
            </Typography.Text>
            <Typography.Text type="secondary" code>
              {error.message}
            </Typography.Text>
            <Button size="small" onClick={() => this.setState({ error: null })}>
              Try showing it again
            </Button>
          </Space>
        }
      />
    );
  }
}

export default ScenarioErrorBoundary;
